/*
 * THE PROSPECT ROOM, IN THE CRM'S WORDS: every line the panel, the dialogs and
 * the Prospect rooms page print, and the email trim's cutting. Pure, so
 * test/room-model.test.js pins it. Drawn in the Prospect Room canvas, part 2
 * (RoomPanel, AddToRoom, RecapPreview, EmailTrim, EditDialogs, RoomsList).
 */
import { shortDate, plural } from "../record/format.mjs";
import { splitSignature } from "../timelineModel.mjs";

export const SECTIONS = ["decks", "pricing", "recaps", "agreement", "conversation"];

export const SECTION_LABEL = {
  decks: "Decks & examples",
  pricing: "Pricing",
  recaps: "Call recaps",
  agreement: "Agreement",
  conversation: "Our conversation",
};

const SECTION_SHORT = { decks: "Decks", pricing: "Pricing", recaps: "Call recaps", agreement: "Agreement", conversation: "Conversation" };

// The link under each section's head in the panel (RoomPanel board).
export const SECTION_ACTION = {
  decks: "+ From library",
  pricing: "Update from What we quoted",
  recaps: "+ From a follow-up",
  agreement: "+ PandaDoc link",
  conversation: "+ From the timeline",
};

export const KIND_LABEL = { deck: "Deck", case_study: "Case study", example_ads: "Example ads", link: "Link" };
export const KIND_FILTERS = [
  { key: "all", label: "All" },
  { key: "deck", label: "Decks" },
  { key: "case_study", label: "Case studies" },
  { key: "example_ads", label: "Example ads" },
  { key: "link", label: "Links" },
];

/** "4m 10s", "2m", "40s". */
export function duration(secs) {
  const s = Math.round(Number(secs) || 0);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r >= 5 ? `${m}m ${r}s` : `${m}m`;
}

export function ordinal(n) {
  const v = n % 100;
  const suffix = v >= 11 && v <= 13 ? "th" : ["th", "st", "nd", "rd"][n % 10] || "th";
  return `${n}${suffix}`;
}

const quoted = (text, n = 32) => {
  const one = String(text || "").replace(/\s+/g, " ").trim();
  if (!one) return null;
  return `“${one.length > n ? `${one.slice(0, n - 1).trimEnd()}…` : one}”`;
};

/* ------------------------------------------------------------- stats -- */

/** "Opened 5 times by 3 people · last Oct 14 · most time on Pricing", or the empty state. */
export function statsLine(stats, { now = new Date() } = {}) {
  if (!stats || !stats.opens) return "Not opened yet";
  const parts = [`Opened ${plural(stats.opens, "time")} by ${plural(stats.people, "person", "people")}`];
  if (stats.last_at) parts.push(`last ${shortDate(stats.last_at, { now })}`);
  if (stats.top) parts.push(`most time on ${SECTION_LABEL[stats.top]}`);
  return parts.join(" · ");
}

export function statChips(stats) {
  return ((stats && stats.chips) || []).map((c) => ({ ...c, text: `${SECTION_SHORT[c.section] || c.label} ${duration(c.seconds)}` }));
}

/* ------------------------------------------------------------- people -- */

const EVENT_WORDS = {
  open: (e) => `Opened ${e.label ? `“${e.label}”` : "a deck"}`,
  download: (e) => `Downloaded ${e.label ? `“${e.label}”` : "a file"}`,
  book: () => "Clicked Book a call",
  sign: () => "Clicked Review & sign in PandaDoc",
  ready: (e) => `Pressed ${e.label ? `“${e.label}”` : "the next-step button"}`,
  step: (e) => `Ticked ${e.label ? `“${e.label}”` : "a step"}`,
};

/** "Opened “Remotion intro deck”", "Clicked Book a call". */
export function eventWords(e) {
  const f = EVENT_WORDS[e.kind];
  return (f ? f(e) : e.kind) + (e.other_browser ? " · another browser" : "");
}

/** One person's line: "3 opens · 2 browsers · last Oct 15 · Pricing 4m 10s". */
export function personLine(p, { now = new Date() } = {}) {
  if (!p.opens) return p.code ? "Link made, not opened yet" : "Not opened yet";
  const parts = [plural(p.opens, "open")];
  if (p.browsers > 1) parts.push(`${p.browsers} browsers`);
  if (p.last_at) parts.push(`last ${shortDate(p.last_at, { now })}`);
  const top = Object.entries(p.seconds || {}).filter(([, s]) => s >= 5).sort((a, b) => b[1] - a[1])[0];
  if (top) parts.push(`${SECTION_SHORT[top[0]] || top[0]} ${duration(top[1])}`);
  return parts.join(" · ");
}

export function personName(p) {
  return p.name || "Without a personal link";
}

/* ------------------------------------------------------- panel rows -- */

function pricingTitle(q) {
  if (!q) return "Pricing";
  const v = q.view || {};
  if (v.rows && v.base_label) {
    const rates = v.rows
      .flatMap((r) => r.fee.filter((p) => p.b).map((p) => p.t))
      .filter(Boolean);
    return rates.length ? `${v.base_label} base + ${rates.join(" / ")} bands` : `${v.base_label} base`;
  }
  return (v.shown || q.lines || [])[0] || "Pricing";
}

/** One row of the panel: { title, meta, edited }. */
/** The demo dashboard on a room (src/rooms/demo.js): a Decks & examples item with no library row. */
export function isDemoItem(item) {
  return !!item && item.section === "decks" && (item.content || {}).kind === "demo";
}

export function itemRow(item, { now = new Date(), host = "Gabriel" } = {}) {
  const d = (ts) => (ts ? shortDate(ts, { now }) : null);
  const c = item.content || {};
  const edited = !!item.edited_at;
  switch (item.section) {
    case "decks": {
      const lib = item.library || {};
      if (isDemoItem(item)) {
        return { title: lib.title || "Demo dashboard", meta: ["Live demo", d(item.created_at), "their link closes when you remove it", quoted(item.note)].filter(Boolean).join(" · "), edited };
      }
      const kind = lib.url ? (lib.link && lib.link.label) || "Link" : [lib.kind_label, lib.detail].filter(Boolean).join(" · ");
      return { title: lib.title || "(removed from the library)", meta: [kind, d(item.created_at), quoted(item.note)].filter(Boolean).join(" · "), edited };
    }
    case "pricing": {
      const p = item.pricing || {};
      const cur = p.current;
      const prev = p.previous;
      const meta = [];
      if (prev) meta.push(`Revised ${d(cur && cur.at) || ""}`.trim(), `replaces ${d(prev.at) || "an earlier quote"}`);
      else meta.push(cur && cur.at ? `From your email of ${d(cur.at)}` : "From the record");
      if (cur && cur.view && cur.view.minimum) meta.push(cur.view.minimum);
      if (prev && p.show_previous === false) meta.push("earlier quote hidden");
      return { title: pricingTitle(cur), meta: meta.join(" · "), edited };
    }
    case "recaps": {
      const src = c.source || {};
      return {
        title: `${c.title || "Call"}${c.date ? ` · ${d(c.date)}` : ""}`,
        meta: src.at ? `Cleaned from your follow-up of ${d(src.at)}` : "Written by hand",
        edited,
      };
    }
    case "agreement": {
      if (c.kind === "pandadoc") {
        const opened = item.opened ? `opened ${d(item.opened.at)}` : "not opened yet";
        return { title: "Review & sign in PandaDoc", meta: [c.sent_at ? `Sent ${d(c.sent_at)}` : null, opened].filter(Boolean).join(" · "), edited };
      }
      return { title: c.label || "Draft agreement (.docx)", meta: [d(c.generated_at || item.created_at), `v${c.version || 1}`].filter(Boolean).join(" · "), edited };
    }
    case "conversation": {
      const from = c.from && c.from.us ? "You" : (c.from && c.from.name) || "Them";
      const meta = [d(c.at)];
      if (c.from && c.from.us && c.to && c.to.length) meta.push(`to ${c.to.join(", ")}`);
      if (c.cut_count) meta.push(`${plural(c.cut_count, "line")} cut`);
      if (String(c.text || "").length > 700) meta.push("folds on the room");
      return { title: `${from}: ${c.subject || "(no subject)"}`, meta: meta.filter(Boolean).join(" · "), edited };
    }
    default:
      return { title: item.section, meta: "", edited };
  }
}

/* ----------------------------------------------------------- recap -- */

export const BLOCKS = [
  { key: "heard", label: "What we heard", rows: 3 },
  { key: "suggested", label: "What we suggested", rows: 3 },
  { key: "pricing", label: "Pricing", rows: 2, hint: "Points to the Pricing section on the room, so numbers live in one place" },
  { key: "next", label: "Next step", rows: 2 },
];

/** "Tue, Oct 13" for the recap's date field. */
export function recapDate(ts) {
  const d = ts ? new Date(ts) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
}

/** The field back to an instant: "Tue, Oct 13" → that day this year, noon. */
export function parseRecapDate(text, { now = new Date(), fallback = null } = {}) {
  const s = String(text || "").trim();
  if (!s) return fallback;
  const iso = /^\d{4}-\d{2}-\d{2}/.test(s) ? new Date(s) : null;
  if (iso && !Number.isNaN(iso.getTime())) return iso.toISOString();
  const clean = s.replace(/^[A-Za-z]{3,9},?\s+/, "");
  const withYear = /\d{4}/.test(clean) ? clean : `${clean} ${now.getFullYear()}`;
  const d = new Date(`${withYear} 12:00`);
  return Number.isNaN(d.getTime()) ? fallback : d.toISOString();
}

/* --------------------------------------------------------- email trim -- */

// The same line-anchored starts as src/rooms/model.js stripQuoted. Anchored to a
// line start because "call on Oct 13, ..." mid-sentence is not a quote.
const QUOTE_LINE_STARTS = [
  /(^|\n)[ \t>]*On\b[^\n]{0,200}(?:\n[^\n]{0,200})?\bwrote:/,
  /(^|\n)[ \t]*-{2,}\s*(?:Original Message|Forwarded message)\s*-{2,}/i,
  /(^|\n)[ \t]*_{10,}/,
  /(^|\n)[ \t]*From:[^\n]*\n(?:[^\n]*\n){0,3}?[ \t]*(?:Sent|Date):/i,
  /(^|\n)[ \t]*Begin forwarded message:/i,
];

const HEADER_LINE = /^[ \t]*(From|Sent|To|Cc|Subject|Date):\s/i;

/**
 * An email's body → what the trim dialog opens with.
 *   { segments: [{ t, cut: false }], auto_cuts: [{ kind, label, text, summary }] }
 * The three automatic cuts (quoted earlier messages, the signature, forwarded
 * headers) are taken out of the body and listed, each restorable. The body is
 * one segment per paragraph, separators kept, so cutting and joining round-trip.
 */
export function trimDraft(body) {
  let text = String(body || "").replace(/\r\n?/g, "\n");
  const auto = [];

  let cut = text.length;
  for (const re of QUOTE_LINE_STARTS) {
    const m = re.exec(text);
    if (m && m.index < cut) cut = m.index + (m[1] ? m[1].length : 0);
  }
  if (cut < text.length && text.slice(0, cut).trim()) {
    const quotedText = text.slice(cut).trim();
    const n = (quotedText.match(/\bwrote:/g) || []).length || 1;
    const first = quotedText.split("\n")[0].trim();
    auto.push({ kind: "quoted", label: "Quoted earlier messages", text: quotedText, summary: `${quoted(first, 40) || "“…”"} · ${plural(n, "message")}` });
    text = text.slice(0, cut);
  }
  // Lines that are themselves quotes ("> …") with the attribution gone.
  const quoteLines = text.split("\n").filter((l) => /^\s*>/.test(l));
  if (quoteLines.length) {
    text = text.split("\n").filter((l) => !/^\s*>/.test(l)).join("\n");
    if (!auto.some((a) => a.kind === "quoted")) {
      auto.push({ kind: "quoted", label: "Quoted earlier messages", text: quoteLines.join("\n"), summary: `${plural(quoteLines.length, "quoted line")}` });
    }
  }

  // A pasted header block inside the body: two or more "From: / To: / Subject:" lines in a row.
  const lines = text.split("\n");
  let headerStart = -1;
  let headerEnd = -1;
  for (let i = 0; i < lines.length; i++) {
    if (HEADER_LINE.test(lines[i])) {
      let j = i;
      while (j < lines.length && HEADER_LINE.test(lines[j])) j++;
      if (j - i >= 2) {
        headerStart = i;
        headerEnd = j;
        break;
      }
      i = j;
    }
  }
  if (headerStart >= 0) {
    const headers = lines.slice(headerStart, headerEnd).join("\n");
    auto.push({ kind: "headers", label: "Forwarded headers", text: headers, summary: quoted(headers.split("\n")[0], 40) });
    lines.splice(headerStart, headerEnd - headerStart);
    text = lines.join("\n");
  }

  text = text.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  const { body: main, signature } = splitSignature(text);
  if (signature) {
    auto.push({ kind: "signature", label: "Signature block", text: signature, summary: quoted(signature.replace(/\n+/g, " · "), 60) });
  }
  return { segments: paragraphSegments(signature ? main : text), auto_cuts: auto };
}

function paragraphSegments(text) {
  const out = [];
  const re = /([\s\S]*?)(\n\s*\n|$)/g;
  let m;
  while ((m = re.exec(text)) && m[0]) {
    out.push({ t: m[1] + m[2], cut: false });
    if (!m[2]) break;
  }
  return out.filter((s) => s.t);
}

/**
 * Cut a selection: from segment `a.i` at offset `a.off` to segment `b.i` at
 * `b.off` (either order). Worked on the flat text, so a selection across
 * paragraphs cuts exactly what was selected; whitespace at either end of the
 * selection stays, so the "…" sits where the words were. Neighbours with the
 * same state merge.
 */
export function cutRange(segments, a, b) {
  const abs = (p) => segments.slice(0, p.i).reduce((n, s) => n + s.t.length, 0) + p.off;
  let from = Math.min(abs(a), abs(b));
  let to = Math.max(abs(a), abs(b));
  const text = segments.map((s) => s.t).join("");
  while (from < to && /\s/.test(text[from])) from++;
  while (to > from && /\s/.test(text[to - 1])) to--;
  if (from >= to) return segments;
  const flags = [];
  for (const s of segments) for (let k = 0; k < s.t.length; k++) flags.push(s.cut);
  for (let k = from; k < to; k++) flags[k] = true;
  return runs(text, flags);
}

/** Put a hand cut back. */
export function restoreCut(segments, i) {
  return merge(segments.map((s, k) => (k === i ? { ...s, cut: false } : { ...s })));
}

function runs(text, flags) {
  const out = [];
  for (let k = 0; k < text.length; k++) {
    const prev = out[out.length - 1];
    if (prev && prev.cut === flags[k]) prev.t += text[k];
    else out.push({ t: text[k], cut: flags[k] });
  }
  return out;
}

function merge(segs) {
  const out = [];
  for (const s of segs) {
    if (!s.t) continue;
    const prev = out[out.length - 1];
    if (prev && prev.cut === s.cut) prev.t += s.t;
    else out.push({ ...s });
  }
  return out;
}

/** What the room will show: kept text, a hand cut as one quiet "…", restored automatic cuts at the end. */
export function keptText(segments, restored = []) {
  let out = "";
  let lastCut = false;
  for (const seg of segments || []) {
    if (!seg || typeof seg.t !== "string") continue;
    if (seg.cut) {
      if (!lastCut) out = out.replace(/[ \t]+$/, "") + (/\n$/.test(out) || !out ? "…" : " …");
      lastCut = true;
      continue;
    }
    if (lastCut && seg.t && !/^\s/.test(seg.t)) out += " ";
    out += seg.t;
    lastCut = false;
  }
  for (const r of restored || []) {
    if (r && typeof r.text === "string" && r.text.trim()) out = `${out.trimEnd()}\n\n${r.text.trim()}`;
  }
  return out.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

/* ------------------------------------------------------------- rooms list -- */

const STALE_DAYS = 3;

/** The Items column: "11 · 5 decks, 1 price, 2 recaps, 2 agreement, 3 emails". */
export function itemsWords(counts) {
  const c = counts || {};
  const total = SECTIONS.reduce((n, s) => n + (c[s] || 0), 0);
  if (!total) return "Empty";
  const words = [];
  if (c.decks) words.push(plural(c.decks, "deck"));
  if (c.pricing) words.push(plural(c.pricing, "price"));
  if (c.recaps) words.push(plural(c.recaps, "recap"));
  if (c.agreement) words.push(`${c.agreement} agreement`);
  if (c.conversation) words.push(plural(c.conversation, "email"));
  return `${total} · ${words.join(", ")}`;
}

/** Never opened, shared more than three days ago: the cue to nudge them. */
export function notOpenedTag(row, { now = new Date() } = {}) {
  if (row.last_opened_at || !row.first_shared_at || !row.enabled) return null;
  const age = (now - new Date(row.first_shared_at)) / 86400000;
  return age > STALE_DAYS ? `Not opened yet · shared ${shortDate(row.first_shared_at, { now })}` : null;
}

export function lastOpenedWords(ts, { now = new Date() } = {}) {
  if (!ts) return "Never";
  const d = new Date(ts);
  const sameDay = d.toDateString() === now.toDateString();
  const hm = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  return sameDay ? `Today ${hm}` : shortDate(ts, { now });
}

export const LIST_FILTERS = [
  { key: "all", label: "All" },
  { key: "week", label: "Opened this week" },
  { key: "never", label: "Never opened" },
  { key: "off", label: "Off" },
];

export function filterRooms(rows, key, { now = new Date() } = {}) {
  const week = now.getTime() - 7 * 86400000;
  switch (key) {
    case "week":
      return rows.filter((r) => r.last_opened_at && new Date(r.last_opened_at).getTime() >= week);
    case "never":
      return rows.filter((r) => !r.last_opened_at);
    case "off":
      return rows.filter((r) => !r.enabled);
    default:
      return rows;
  }
}
